/**
 * TODO: This module should be called `members.ts` since it pertains to member
 * management and we're going to have a lot more of it soon.
 */

import type { AxiosResponse } from "axios";

import { axiosInstance, computeHeaders } from "./axios";
import {
  BatchInvite,
  RawBatchInviteRequest,
  InvitesNetworkResponse,
  DataForCreateInviteNetworkRequest,
  CreateInviteNetworkResponse,
  RawBatchInviteNetworkResponse,
} from "../models/invitations";
import { InternalRole } from "../models/permissions";
import { ensureError } from "./util";
import { getIsAxiosError } from "./granularAxios";
import { refinedListOfBatchInvites } from "./helpers/refinements";

const getInvitations = async ({
  authToken,
  userId,
  operationId,
  locale,
}: {
  authToken: string;
  userId: string;
  operationId: string;
  locale: string;
}): Promise<AxiosResponse<InvitesNetworkResponse> | null> => {
  /**
   * NOTE: The /v2 endpoint is to be used *only* when fetching a list of
   * invitations. It was created just for the Core. Other endpoints (like
   * add/remove member) do not exist under /v2.
   *
   * NOTE: Upstream *does not check* for the validity of an Operation ID!
   *
   * You can supply an Operation ID like this
   * "00000000-0000-0000-0000-000000000000" and you will see an empty list.
   * This was done to prevent another call to the Roles Service (to ascertain
   * the existence and ownership of an Operation.)
   *
   * Also note that, *in the Core only and for #simplicity*, we return null
   * to represent an empty list if the user is not allowed to view invites for an operation.
   *
   * This is not a terrible decision. In a UI, you would simply not render an
   * invites component if `!isAdmin` 🤷‍♂️
   */
  try {
    const response = await axiosInstance.request<
      never,
      AxiosResponse<InvitesNetworkResponse>
    >({
      method: "GET",
      url: `/api/invite-svc/v2/invites/operation/${operationId}`,
      headers: computeHeaders({
        authToken,
        userId,
        operationId,
        locale,
      }),
    });
    return response;
  } catch (_e) {
    const e = ensureError(_e);
    if (getIsAxiosError(e) && e.response && e.response.status === 403) {
      return null;
    }
    throw e;
  }
};

const inviteMember = async ({
  authToken,
  userId,
  operationId,
  locale,
  as,
  withPhoneOrEmail,
  inviteLinkUrl,
}: {
  authToken: string;
  userId: string;
  operationId: string;
  locale: string;
  as: InternalRole;
  withPhoneOrEmail: string;
  inviteLinkUrl?: string;
}): Promise<AxiosResponse<CreateInviteNetworkResponse>> => {
  const response = await axiosInstance.post<
    DataForCreateInviteNetworkRequest,
    AxiosResponse<CreateInviteNetworkResponse>
  >(
    "/api/invite-svc/invite",
    {
      operationId,
      roleName: as,
      userName: withPhoneOrEmail,
      inviteLinkUrl,
    },
    {
      headers: computeHeaders({
        authToken,
        userId,
        operationId,
        locale,
      }),
    },
  );

  return response;
};

/**
 * Returning a stupid-simple Axios `Promise` is what you would do _normally_.
 * But this batch invites case is a bit weird. See normally, we just try/catch
 * and use the `catch` clause for *server* errors (HTTP 5xx.) In the batch
 * invites case, we need to `catch` *client* errors as well (HTTP 4xx.) This
 * has to do with exactly ONE case: the phone number is well formed
 * (+19999999999) but is not valid ("999" is not a valid area code in 'MURCA.)
 *
 * So that's what all this faffing about is about.
 *
 * In this case, if the client makes a batch invite payload that produces an
 * HTTP 400, *it is all-or-nothing*! So what we do in this case is return the
 * *entire* faulty payload with an extra property on each invite called
 * `message` that indicates the error. Like we noted earlier, this will happen
 * in exactly one case: The phone number is well-formed but invalid crap.
 *
 * So use the refinement defined above to produce a `BatchInvite` object with
 * the error message and call it a day.
 */
const inviteMembers = async ({
  authToken,
  userId,
  operationId,
  locale,
  invites,
}: {
  authToken: string;
  userId: string;
  operationId: string;
  locale: string;
  invites: RawBatchInviteRequest["invites"];
}): Promise<BatchInvite[]> => {
  try {
    const invitesRequest: RawBatchInviteRequest = {
      invites,
    };
    const r = await axiosInstance.post<RawBatchInviteNetworkResponse>(
      "/api/invite-svc/v2/invites",
      invitesRequest,
      {
        headers: computeHeaders({
          authToken,
          userId,
          operationId,
          locale,
        }),
      },
    );
    return refinedListOfBatchInvites(r.data);
  } catch (_e) {
    const e = ensureError(_e);
    if (getIsAxiosError(e) && e.response && e.response.status === 400) {
      const failedInvites = refinedListOfBatchInvites(
        e.response.data as RawBatchInviteNetworkResponse,
      );
      return failedInvites;
    }
    throw e;
  }
};

const removeInvitation = async ({
  authToken,
  userId,
  operationId,
  locale,
  username,
}: {
  authToken: string;
  userId: string;
  operationId: string;
  locale: string;
  username: string;
}): Promise<void> => {
  await axiosInstance.request({
    method: "DELETE",
    url: `/api/invite-svc/invites/operation/${operationId}/user/${username}`,
    headers: computeHeaders({
      authToken,
      userId,
      operationId,
      locale,
    }),
  });
};

const removeCortevaMembersFromOperation = async ({
  authToken,
  userId,
  operationId,
  copyOperation,
  removalReason,
}: {
  authToken: string;
  userId: string;
  operationId: string;
  copyOperation: boolean;
  removalReason: string;
}): Promise<void> => {
  await axiosInstance.request({
    method: "DELETE",
    url: `/api/roles-svc/v2/operation/${operationId}/corteva_user`,
    headers: computeHeaders({
      authToken,
      userId,
      operationId,
    }),
    data: {
      allow_copy_operation: copyOperation,
      removal_reason: removalReason,
    },
  });
};

export default {
  getInvitations,
  inviteMembers,
  inviteMember,
  removeCortevaMembersFromOperation,
  removeInvitation,
};
